import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ZoneRowsEditor from "@/components/patients/ZoneRowsEditor";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toLocalIsoString } from "@/lib/agendaConstants";
import { PATIENTS, TREATMENTS } from "@/constants/testIds";

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  { value: "zelle", label: "Zelle" },
  { value: "other", label: "Otro" },
];

/** Registrar un tratamiento aplicado (ficha tecnica): zonas de aplicacion con
 * unidades propias que descuentan automaticamente el producto principal del
 * inventario, forma de pago (vinculada a un pago real, sin duplicar si el
 * turno ya tiene uno registrado), y otros insumos adicionales. */
export default function TreatmentModal({ open, onOpenChange, patientId, professionals, services, products, currentUser, defaults, onCreated }) {
  const isProfessional = currentUser?.role === "professional";
  const EMPTY_FORM = {
    professional_id: isProfessional ? currentUser.professional_id || "" : "",
    service_id: "",
    appointment_id: null,
    product_notes: "",
    observations: "",
    products_used: [],
    treatment_date: toLocalIsoString(new Date()),
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [zones, setZones] = useState([]);
  const [primaryProductId, setPrimaryProductId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("none");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [zoneCatalog, setZoneCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, ...(defaults || {}) });
      setZones([]);
      setPrimaryProductId("");
      setPaymentMethod("none");
      setPaymentAmount("");
      setError("");
      api.get("/zones").then(({ data }) => setZoneCatalog(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaults]);

  const selectedService = services.find((s) => s.id === form.service_id);

  const suggestedZoneNames = useMemo(() => {
    if (!selectedService) return [];
    if (selectedService.suggested_zone_ids?.length > 0) {
      return zoneCatalog.filter((z) => selectedService.suggested_zone_ids.includes(z.id)).map((z) => z.name);
    }
    if (selectedService.category) {
      return zoneCatalog.filter((z) => z.categories?.includes(selectedService.category)).map((z) => z.name);
    }
    return [];
  }, [selectedService, zoneCatalog]);

  const handleSave = async () => {
    if (!form.professional_id) {
      setError("Selecciona el profesional que aplico el tratamiento");
      return;
    }
    const validZones = zones.filter((z) => z.zone_name && Number(z.units) > 0);
    setSaving(true);
    setError("");
    try {
      const payload = {
        professional_id: form.professional_id,
        appointment_id: form.appointment_id || null,
        service_id: form.service_id || null,
        zones: validZones.map((z) => ({ zone_name: z.zone_name, units: Number(z.units), notes: z.notes || null })),
        primary_product_id: primaryProductId || null,
        product_notes: form.product_notes || null,
        observations: form.observations || null,
        products_used: form.products_used.filter((r) => r.product_id).map((r) => ({ product_id: r.product_id, quantity: Number(r.quantity) || 1 })),
        treatment_date: form.treatment_date,
        new_payment: paymentMethod !== "none" && paymentAmount ? { method: paymentMethod, amount: Number(paymentAmount) } : null,
      };
      const { data } = await api.post(`/patients/${patientId}/treatments`, payload);
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addProductRow = () => {
    setForm((f) => ({ ...f, products_used: [...f.products_used, { product_id: products[0]?.id || "", quantity: 1 }] }));
  };
  const updateProductRow = (index, patch) => {
    setForm((f) => ({ ...f, products_used: f.products_used.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));
  };
  const removeProductRow = (index) => {
    setForm((f) => ({ ...f, products_used: f.products_used.filter((_, i) => i !== index) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={PATIENTS.treatmentModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Registrar tratamiento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Profesional que lo aplico</Label>
            <Select value={form.professional_id} onValueChange={(v) => setForm((f) => ({ ...f, professional_id: v }))} disabled={isProfessional}>
              <SelectTrigger data-testid={PATIENTS.treatmentModalProfessionalSelect}><SelectValue placeholder="Seleccionar profesional" /></SelectTrigger>
              <SelectContent>
                {professionals.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Servicio / tratamiento del catalogo (opcional)</Label>
            <Select value={form.service_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid={PATIENTS.treatmentModalServiceSelect}><SelectValue placeholder="Ninguno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ninguno</SelectItem>
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedService?.review_included_days > 0 && (
              <p className="text-xs text-emerald-700 mt-1">Incluye revisión sin cargo dentro de {selectedService.review_included_days} días.</p>
            )}
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha y hora</Label>
            <Input type="datetime-local" value={form.treatment_date} onChange={(e) => setForm((f) => ({ ...f, treatment_date: e.target.value }))} data-testid={PATIENTS.treatmentModalDateInput} />
          </div>

          <ZoneRowsEditor
            zones={zones}
            setZones={setZones}
            primaryProductId={primaryProductId}
            setPrimaryProductId={setPrimaryProductId}
            products={products || []}
            zoneCatalog={zoneCatalog}
            suggestedZoneNames={suggestedZoneNames}
          />

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Producto usado (si no esta en el catalogo de inventario)</Label>
            <Input value={form.product_notes} onChange={(e) => setForm((f) => ({ ...f, product_notes: e.target.value }))} data-testid={PATIENTS.treatmentModalProductInput} placeholder="Ej: Botox Allergan, 1 vial" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm text-muted-foreground block">Otros insumos consumidos (opcional)</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addProductRow} disabled={!products || products.length === 0}>
                Agregar insumo
              </Button>
            </div>
            {form.products_used.length > 0 && (
              <div className="space-y-2">
                {form.products_used.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={row.product_id} onValueChange={(v) => updateProductRow(idx, { product_id: v })}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Producto" /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (stock: {p.stock_quantity} {p.unit})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" min="0" step="0.01" value={row.quantity} className="w-24"
                      onChange={(e) => updateProductRow(idx, { quantity: e.target.value })}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeProductRow(idx)}>×</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Forma de pago (si se cobró ahora)</Label>
            <div className="grid grid-cols-2 gap-4">
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid={TREATMENTS.paymentMethodSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin pago ahora</SelectItem>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                type="number" min="0" step="0.01" placeholder="Monto"
                value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                disabled={paymentMethod === "none"}
                data-testid={TREATMENTS.paymentAmountInput}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1" data-testid={TREATMENTS.paymentExistingNotice}>
              Si el turno ya tiene un pago registrado, se vincula automáticamente y esto se ignora (no duplica el cobro).
            </p>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Observaciones</Label>
            <Textarea value={form.observations} onChange={(e) => setForm((f) => ({ ...f, observations: e.target.value }))} data-testid={PATIENTS.treatmentModalObservationsInput} rows={3} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PATIENTS.treatmentModalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={PATIENTS.treatmentModalSaveButton}>
            {saving ? "Guardando..." : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
